import { TEST_IDS, userActions } from '@va/constants';
import { FilterIcon, PencilEditIcon } from '@va/icons';
import { useLocale, useTranslate } from '@va/localization';
import { hasPermission } from '@va/standalone/shared/selectors';
import { FilterTemplate } from '@va/types/filter-templates';
import {
  Button,
  IconWithBackground,
  ParagraphWithTooltip,
  RadioInputButton,
  fontWeights,
  paragraphSizes,
} from '@va/ui/design-system';
import classNames from 'classnames';
import moment from 'moment';
import { useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';

export const TemplateListItem = ({
  template: { name, createdAt, id },
  selectedTemplateId,
  onClickEdit,
  onClick,
  focused,
  resetFocus,
}: {
  template: FilterTemplate;
  selectedTemplateId: string | undefined;
  onClickEdit: () => void;
  onClick: () => void;
  focused?: boolean;
  resetFocus?: () => void;
}) => {
  const isSelected = useMemo(() => id === selectedTemplateId, [id, selectedTemplateId]);
  const ref = useRef<HTMLLIElement>(null);
  const translate = useTranslate();
  const { locale } = useLocale();
  const hasEditPermission = useSelector((state) => hasPermission(state, userActions.Edit));

  return (
    <li
      ref={ref}
      id={id}
      role='option'
      aria-selected={false}
      data-testid={TEST_IDS.helpers.createListItemId(id)}
      className='overflow-hidden rounded-xl flex items-center gap-3 bg-white hover:bg-white-snow px-4 group'
      onClick={onClick}
    >
      <RadioInputButton className='w-4 h-4 shrink-0' selected={isSelected} />
      <div className='flex gap-3 items-center overflow-hidden'>
        <IconWithBackground
          className='bg-white-snow! group-hover:bg-white! shrink-0 w-9! h-9!'
          icon={() => <FilterIcon className='w-3 h-3' color='#3C3C3C ' />}
        />
        <div className='overflow-hidden'>
          <ParagraphWithTooltip
            size={paragraphSizes.tiny}
            colorClassName='text-gray-charchoal'
            weight={fontWeights.medium}
          >
            {name}
          </ParagraphWithTooltip>
          <ParagraphWithTooltip
            size={paragraphSizes.extraTiny}
            colorClassName='text-gray-devil'
            weight={fontWeights.medium}
          >
            {translate('all.filterTemplates.createdOn', {
              date: moment(createdAt).locale(locale).format('DD MMM YYYY'),
            })}
          </ParagraphWithTooltip>
        </div>
      </div>
      <Button
        className={classNames('ml-auto invisible group-hover:visible shrink-0 scale-75', {
          'visible!': focused,
        })}
        color='quaternary'
        onClick={onClickEdit}
        icon={(_, color) => <PencilEditIcon color={color} />}
        disabled={!hasEditPermission}
        tooltip={!hasEditPermission && translate('tooltips.contributorRole')}
        onBlur={resetFocus}
      />
    </li>
  );
};
